import React from 'react';

import {
  Button,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@eluve/components';
import { pluralize } from '@eluve/utils';

export interface GlobalBundleBlocker {
  id: string;
  displayName: string | null;
  tenantTemplateBundlesAggregate: { aggregate: { count: number } | null };
}

/**
 * A row of `getActiveBundleBlockersQuery`: an active bundle still held open by
 * an enabled tenant bundle, tagged with every FK so a page can group by the
 * template/model id its rows key on.
 */
export interface ActiveBundleBlockerRow extends GlobalBundleBlocker {
  promptTemplateId: string;
  outputTemplateId: string | null;
  modelArgsId: string;
}

/**
 * Indexes the page-level blocker rows by the FK the list keys on (prompt /
 * output template / model id) so each row's toggle can find its blockers in
 * O(1). Returns an empty map when `rows` is null/empty.
 */
export const groupGlobalBlockersByFk = (
  rows: readonly ActiveBundleBlockerRow[] | null | undefined,
  getKey: (row: ActiveBundleBlockerRow) => string | null,
): Map<string, GlobalBundleBlocker[]> => {
  const map = new Map<string, GlobalBundleBlocker[]>();
  for (const row of rows ?? []) {
    const key = getKey(row);
    if (!key) continue;
    const entry: GlobalBundleBlocker = {
      id: row.id,
      displayName: row.displayName,
      tenantTemplateBundlesAggregate: row.tenantTemplateBundlesAggregate,
    };
    const existing = map.get(key);
    if (existing) {
      existing.push(entry);
    } else {
      map.set(key, [entry]);
    }
  }
  return map;
};

const DEFAULT_INSTRUCTION =
  'Deactivate these active bundles (or disable them per organization) before deactivating this:';

export interface GlobalBundleBlockerTooltipProps {
  /** Active bundles still depending on the entity being deactivated. */
  blockers: readonly GlobalBundleBlocker[];
  /** aria-label for the disabled button (matches the active-state label). */
  label: string;
  /** The icon/content rendered inside the disabled button. */
  children: React.ReactNode;
  /**
   * Lead-in line above the bundle list. Defaults to the enabled-tenant wording
   * (deactivate OR disable per organization). Callers whose blocking scope is
   * any-active-bundle — where disabling per organization does NOT clear the
   * block (e.g. model args, ENG-4409) — should pass wording that only mentions
   * deactivating the bundles.
   */
  instruction?: React.ReactNode;
}

/**
 * Renders the disabled deactivate button + hover tooltip shown when an
 * active prompt template / output template / model still has enabled tenant
 * bundles depending on it. Shared by the three global admin toggle actions
 * (prompt template, output template, model args). Blockers are resolved by the
 * list page's `getActiveBundleBlockersQuery` and passed down, so this only
 * renders once a blocker is known to exist.
 */
export const GlobalBundleBlockerTooltip: React.FC<
  GlobalBundleBlockerTooltipProps
> = ({ blockers, label, children, instruction = DEFAULT_INSTRUCTION }) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <Button variant="outline" aria-label={label} disabled>
        {children}
      </Button>
    </TooltipTrigger>
    <TooltipContent>
      <div>
        <div>{instruction}</div>
        <ul className="mt-1 list-disc pl-4">
          {blockers.map((bundle) => {
            const orgCount =
              bundle.tenantTemplateBundlesAggregate.aggregate?.count ?? 0;
            return (
              <li key={bundle.id}>
                {bundle.displayName ?? bundle.id} (
                {pluralize(orgCount, 'organization')})
              </li>
            );
          })}
        </ul>
      </div>
    </TooltipContent>
  </Tooltip>
);
